// Building_ModUI.tsx — Buildings Set1 §8 editor for `Mod_Building`. The anchor of a building
// tree: owns `fallbackFor` (prototype mode), `roofEnabled`, and the grouped `defaults` editor
// (Level / Room / Window / Door / Roof / Materials tabs). P2 adds the 2D active-floor stepper +
// the level-stacking diagnostics list.
import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, Column, Row, RowLR, Select, Text} from "react-vcomponents";
import {ErrorBoundary, ES, TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {BuildingFallbackFor, BuildingDefaults, Mod_Building, WindowDefaults, DoorDefaults, OpeningDefaults, RoofDefaults, RoomDefaults, LevelDefaults} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Building.js";
import {BuildingShapeSourceKind, BuildingMaterials, DoorSwing, DoorSwingInfo, RoofStyle} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Building/BuildingShared.js";
import {ResolveBuildingDefaults} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {ResolveBuildingFloors, ResolveBuildingGeometryPlan} from "../../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import type {BuildingGeometryPlan} from "../../../../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {InheritableField} from "./Building/InheritableField.js";

type DefaultsTab = "level"|"room"|"window"|"door"|"opening"|"roof"|"materials";
const DefaultsTabs: Array<{value: DefaultsTab, name: string}> = [
	{value: "level", name: "Level"},
	{value: "room", name: "Room"},
	{value: "window", name: "Window"},
	{value: "door", name: "Door"},
	{value: "opening", name: "Opening"},
	{value: "roof", name: "Roof"},
	{value: "materials", name: "Materials"},
];

export const Building_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, obj, mapState} = props;
	const Change = (mutator: (data: Mod_Building)=>any)=>ChangeModWrapper(a=>mutator(a.building!));
	const data = mod.building!;

	const resolvedDefaults = ResolveBuildingDefaults(obj);
	const prov = (path: string)=>resolvedDefaults.provenance[path];

	const [tab, setTab] = useState<DefaultsTab>("level");

	// P2 — level-stacking diagnostics + the 2D active-floor stepper.
	const floorsRes = ResolveBuildingFloors(obj);
	const floorNumbers = [...floorsRes.floors.keys()].sort((a, b)=>a - b);

	// Set2 F4 §10 — the FULL geometry-plan diagnostics. Pre-F4 this panel showed only `ResolveBuildingFloors().warnings` (level stacking), so every Set2 wall diagnostic (thickness conflicts, T-junctions, collinear overlaps, thin cavities, opening-binding, roof) was computed and then silently dropped. `ResolveBuildingGeometryPlan` already aggregates all of them (§8), so the panel just reads it, GROUPED by agent (a floor-plan bug and a stacking bug are different problems; an ungrouped 20-line list reads as noise).
	const plan = ResolveBuildingGeometryPlan(obj);
	const warningGroups = BuildWarningGroups(floorsRes.warnings, plan?.warnings ?? []);

	return (
		<>
			{data.fallbackFor != null &&
				<Row mt={5} style={{backgroundColor: "rgba(200,120,50,.25)", padding: "4px 8px", borderRadius: 4}}>
					<Text style={{color: "#fca"}}>⚠ This is a PROTOTYPE — it generates no geometry. Other Buildings read its null-field defaults from it.</Text>
				</Row>}
			<Text style={{color: "#999", fontSize: 12}}>The anchor of a building tree (floors/walls/roofs). P2: authoring + 2D plan overlays.</Text>

			<Row mt={5}>
				<TextPlus info={`
					When non-null, this Building generates no geometry — it only supplies inherited defaults to other Buildings.
					'descendants' = its own descendants; 'global' = the whole map (lowest orderKey wins).
				`.AsMultiline(0)}>Fallback for:</TextPlus>
				<Select displayType="button bar"
					options={[{name: "None (generates geometry)", value: null}, ...GetEntries(BuildingFallbackFor, "ui")]}
					value={data.fallbackFor ?? null}
					onChange={val=>Change(a=>a.fallbackFor = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info="When false, the roof generator is skipped for this building (walls/floors still build).">Roof enabled:</TextPlus>
				<Button text={data.roofEnabled == false ? "Off" : "On"}
					style={{backgroundColor: data.roofEnabled == false ? "rgba(200,100,100,.4)" : "rgba(100,200,100,.4)"}}
					onClick={()=>Change(a=>a.roofEnabled = data.roofEnabled == false ? null : false)}/>
			</Row>

			{/* P2 — active floor (2D plan view) stepper. */}
			<Row mt={5} style={{alignItems: "center", gap: 4}}>
				<Text style={{color: "#ccc"}}>Active floor (2D plan):</Text>
				<Button text="−" title="Previous floor" enabled={floorNumbers.length > 0}
					onClick={()=>SetActiveFloor(mapState, floorNumbers, -1)}/>
				<Text style={{minWidth: 30, textAlign: "center"}}>{mapState.buildings.activeFloor}</Text>
				<Button text="+" title="Next floor" enabled={floorNumbers.length > 0}
					onClick={()=>SetActiveFloor(mapState, floorNumbers, +1)}/>
			</Row>
			{floorNumbers.length == 0 &&
				<Text style={{color: "#888", fontSize: 11}}>No levels claimed yet — add a Level modifier under this building to populate floors.</Text>}

			{/* P2 + Set2 F4 — the diagnostics list (the main authoring feedback surface). */}
			{warningGroups.length > 0 &&
				<Column mt={6} style={{backgroundColor: "rgba(200,60,60,.12)", padding: "4px 8px", borderRadius: 4}}>
					<Row style={{alignItems: "baseline", gap: 6}}>
						<Text style={{color: "#fca", fontWeight: "bold"}}>Warnings:</Text>
						<Text style={{color: "#b88", fontSize: 11}}>({warningGroups.reduce((n, g)=>n + g.items.length, 0)})</Text>
					</Row>
					{warningGroups.map(group=>
						<Column key={group.label} mt={2}>
							<Text style={{color: "#fc9", fontSize: 11, fontWeight: "bold"}}>{group.label}:</Text>
							{group.items.map((w, i)=><Text key={i} style={{color: "#fbb", fontSize: 11}}>• {w}</Text>)}
						</Column>)}
				</Column>}
			{plan != null && warningGroups.length == 0 && floorNumbers.length > 0 &&
				<Row mt={6}><Text style={{color: "#8c8", fontSize: 11}}>✓ No wall/floor/roof diagnostics.</Text></Row>}

			{/* Set2 F4 §7 — geometry-sharing readout. A floor's `geometryKey` is what lets a 100-storey tower build its walls once, so showing the distinct-key count makes a broken sharing key (one that accidentally includes Z, or collides two floors) visible as a number instead of a silent perf/visual regression. */}
			{plan != null && plan.floors.length > 0 && <GeometrySharingReadout plan={plan}/>}

			<Row mt={8}>
				<Text style={{color: "#ccc", fontWeight: "bold"}}>Defaults:</Text>
			</Row>
			<Row mt={3}>
				<Select displayType="button bar" options={DefaultsTabs} value={tab} onChange={val=>setTab(val)}/>
			</Row>
			<ErrorBoundary key={tab}>
				<DefaultsTabEditor tab={tab} data={data.defaults} resolved={resolvedDefaults.values} prov={prov} Change={Change}/>
			</ErrorBoundary>
		</>
	);
});

/** Set2 F4: a floor's `geometryKey` is the sharing key for its built wall/slab/opening geometry (§6.4/§7), and the renderer also keys its material arrays on it. A broken key is otherwise invisible — a key including Z still RENDERS (just one geometry per floor), and a key colliding two floors renders the WRONG geometry for one of them — so the distinct count is shown next to the floor count. Equal counts mean every floor genuinely repeats; a smaller count is the healthy state for a bespoke building. */
function GeometrySharingReadout(props: {plan: BuildingGeometryPlan}) {
	const {plan} = props;
	const keys = new Set(plan.floors.filter(f=>f.walls != null).map(f=>f.geometryKey));
	const floorsWithWalls = plan.floors.filter(f=>f.walls != null).length;
	const wallSegmentCount = plan.floors.reduce((n, f)=>n + (f.walls?.segments.length ?? 0), 0);
	const openingCount = plan.floors.reduce((n, f)=>n + f.openingsBySegment.reduce((m, g)=>m + g.openings.length, 0), 0);
	return (
		<Column mt={6}>
			<Text style={{color: "#ccc", fontWeight: "bold"}}>Floor plan summary:</Text>
			<Text style={{color: "#aaa", fontSize: 11}}>floors with walls: {floorsWithWalls}; distinct geometries built: {keys.size}{keys.size < floorsWithWalls ? ` (${floorsWithWalls - keys.size} reused)` : ""}</Text>
			<Text style={{color: "#aaa", fontSize: 11}}>merged wall segments: {wallSegmentCount}; bound openings: {openingCount}</Text>
		</Column>
	);
}

/** Set2 F4 §10: group the panel's diagnostics so a long list stays readable. `ResolveBuildingFloors` reports STACKING problems while `ResolveBuildingGeometryPlan.warnings` reports the FLOOR-PLAN problems (walls, openings, roof) and already INCLUDES the stacking list — so partition by inspecting the plan's list rather than printing it twice: anything present in both is shown once, under its own heading. */
function BuildWarningGroups(stackingWarnings: string[], planWarnings: string[]): Array<{label: string, items: string[]}> {
	const stacking = new Set(stackingWarnings);
	const out: Array<{label: string, items: string[]}> = [];
	if (stackingWarnings.length) out.push({label: "Floors / levels", items: stackingWarnings});
	// Wall diagnostics get their own heading because they are the Set2 layer's own report and are the
	// ones an author acts on while drawing rooms.
	const wallPrefixes = ["T-junction:", "Collinear overlapping walls:", "Shared wall thickness conflict", "Thin cavity:"];
	const walls: string[] = [], other: string[] = [];
	for (const w of planWarnings) {
		if (stacking.has(w)) continue; // already shown above
		(wallPrefixes.some(p=>w.startsWith(p)) ? walls : other).push(w);
	}
	if (walls.length) out.push({label: "Walls", items: walls});
	if (other.length) out.push({label: "Other geometry", items: other});
	return out;
}

/** Step the 2D active floor by ±1 within the building's claimed floor-number set (wrapping). */
function SetActiveFloor(mapState: MapState, floorNumbers: number[], direction: number) {
	if (floorNumbers.length == 0) return;
	const cur = mapState.buildings.activeFloor;
	const index = floorNumbers.indexOf(cur);
	const nextIndex = index < 0 ? 0 : (index + direction + floorNumbers.length) % floorNumbers.length;
	mapState.buildings.activeFloor = floorNumbers[nextIndex];
}

function DefaultsTabEditor(props: {tab: DefaultsTab; data: BuildingDefaults; resolved: ReturnType<typeof ResolveBuildingDefaults>["values"]; prov: (path: string)=>string; Change: (mutator: (data: Mod_Building)=>any)=>any}) {
	const {tab, data, resolved, prov, Change} = props;
	const mutate = (mutator: (defs: BuildingDefaults)=>any)=>Change(a=>mutator(a.defaults));

	const numberField = (group: "level"|"room"|"window"|"door"|"opening"|"roof", key: string, label: string, number: {min?: number; max?: number; step?: number})=>{
		const defsGroup = data[group] as any;
		const resolvedValue = (resolved[group] as any)[key] as number;
		const provenance = prov(`${group}.${key}`);
		return <InheritableField key={key} label={label} info="Building-wide default for the whole tree; individual Room/Level/Window/Door can override."
			resolvedValue={resolvedValue} value={defsGroup[key] ?? null} provenance={provenance}
			onChange={(v)=>mutate(d=>{ (d[group] as any)[key] = v as number|n; })}
			number={number}/>;
	};
	const boolField = (group: "level"|"room"|"window"|"door"|"opening"|"roof", key: string, label: string)=>{
		const defsGroup = data[group] as any;
		const resolvedValue = (resolved[group] as any)[key] as boolean;
		const provenance = prov(`${group}.${key}`);
		return <InheritableField key={key} label={label} info="Building-wide default; individual objects can override."
			resolvedValue={resolvedValue} value={defsGroup[key] ?? null} provenance={provenance}
			onChange={(v)=>mutate(d=>{ (d[group] as any)[key] = v == null ? null : !!v; })}/>;
	};

	if (tab == "level") {
		const g = data.level;
		return <>
			{numberField("level", "floorThickness", "Floor thickness (m)", {min: 0.05, max: 2, step: 0.05})}
			{numberField("level", "wallHeight", "Wall height (m)", {min: 1.5, max: 20, step: 0.1})}
			{/* Set2 §4.1: the wall-thickness leaf lives in the `room` slice, but a Level modifier (and
			    these building-wide defaults) source it — so it is edited on the Room tab. */}
			{!g.floorThickness && !g.wallHeight && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "room") {
		const g = data.room;
		return <>
			{numberField("room", "wallThickness", "Wall thickness (m)", {min: 0.05, max: 2, step: 0.05})}
			{boolField("room", "hasFloor", "Has floor")}
			{boolField("room", "hasCeiling", "Has ceiling")}
			{!g.wallThickness && !g.hasFloor && !g.hasCeiling && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "window") {
		const g = data.window;
		return <>
			{numberField("window", "width", "Width (m)", {min: 0.2, max: 6, step: 0.1})}
			{numberField("window", "height", "Height (m)", {min: 0.2, max: 6, step: 0.1})}
			{numberField("window", "sillHeight", "Sill height (m)", {min: 0, max: 6, step: 0.1})}
			{numberField("window", "paneDivisionsX", "Pane divisions (X)", {min: 1, max: 8, step: 1})}
			{numberField("window", "paneDivisionsY", "Pane divisions (Y)", {min: 1, max: 8, step: 1})}
			{!g.width && !g.height && !g.sillHeight && !g.paneDivisionsX && !g.paneDivisionsY && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "door") {
		const g = data.door;
		return <>
			{numberField("door", "width", "Width (m)", {min: 0.5, max: 3, step: 0.1})}
			{numberField("door", "height", "Height (m)", {min: 1.5, max: 4, step: 0.1})}
			{numberField("door", "openAngle", "Open angle (°)", {min: 0, max: 180, step: 5})}
			<Row mt={5}>
				<TextPlus info={DoorSwingInfo} style={{color: "#ccc"}}>Swing:</TextPlus>
				<Select ml={5} displayType="button bar" options={GetEntries(DoorSwing, "ui")}
					value={g.swing ?? resolved.door.swing}
					onChange={val=>mutate(d=>d.door.swing = val)}/>
			</Row>
			{!g.width && !g.height && !g.swing && !g.openAngle && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "opening") {
		const g = data.opening;
		return <>
			{numberField("opening", "width", "Width (m)", {min: 0.2, max: 10, step: 0.1})}
			<Text style={{color: "#888", fontSize: 11}}>An Opening punches out the ENTIRE wall over its width (floor slab top → wall top) — no sill, frame, or model. Use it to stop wall-mesh generation between two Rooms that are part of the same open space.</Text>
			{!g.width && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "roof") {
		const g = data.roof;
		return <>
			<Row mt={5}>
				<Text style={{color: "#ccc"}}>Style:</Text>
				<Select ml={5} displayType="button bar" options={GetEntries(RoofStyle, "ui")}
					value={g.style ?? resolved.roof.style}
					onChange={val=>mutate(d=>d.roof.style = val)}/>
			</Row>
			{(g.style ?? resolved.roof.style) == RoofStyle.gable &&
				boolField("roof", "gableAlongShorter", "Gable along shorter")}
			{numberField("roof", "pitch", "Pitch (rise/run)", {min: 0.05, max: 3, step: 0.05})}
			{numberField("roof", "eaveOverhang", "Eave overhang (m)", {min: 0, max: 5, step: 0.1})}
			{numberField("roof", "fasciaDepth", "Fascia depth (m)", {min: 0, max: 2, step: 0.05})}
			{boolField("roof", "soffitEnabled", "Soffit")}
			{numberField("roof", "ridgeHeightMax", "Ridge height max (m)", {min: 0.5, max: 40, step: 0.5})}
			{numberField("roof", "latticeStep", "Lattice step (m)", {min: 0.1, max: 5, step: 0.1})}
			{!g.style && !g.gableAlongShorter && !g.pitch && !g.eaveOverhang && !g.fasciaDepth && !g.soffitEnabled && !g.ridgeHeightMax && !g.latticeStep && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	if (tab == "materials") {
		const g = data.materials;
		const matFields: Array<[keyof BuildingMaterials, string]> = [
			["floorTop", "Floor top"], ["floorUnderside", "Floor underside"], ["floorEdge", "Floor edge"], ["ceiling", "Ceiling"],
			["wallExterior", "Wall exterior"], ["wallInterior", "Wall interior"], ["wallTop", "Wall top"], ["wallReveal", "Wall reveal"],
			["roofSurface", "Roof surface"], ["roofFascia", "Roof fascia"], ["roofSoffit", "Roof soffit"],
			["doorFrame", "Door frame"], ["doorSlab", "Door slab"], ["doorHardware", "Door hardware"],
			["windowFrame", "Window frame"], ["windowGlass", "Window glass"],
		];
		const anySet = matFields.some(([k])=>(g[k] ?? null) != null);
		return <>
			<Text style={{color: "#999", fontSize: 11}}>Material recipe ids (from the proc-mat catalogue). Full recipe dropdown lands in P6.</Text>
			{matFields.map(([key, label])=>{
				const resolvedValue = (resolved.materials as any)[key] as string;
				const provenance = prov(`materials.${key}`);
				return <InheritableField key={key} label={label} text info="Recipe id inherited via the fallback chain."
					resolvedValue={resolvedValue} value={g[key] ?? null} provenance={provenance}
					onChange={(v)=>mutate(d=>{ (d.materials as any)[key] = v == null ? null : String(v); })}/>;
			})}
			{!anySet && <Text style={{color: "#888", fontSize: 11}}>All inherited.</Text>}
		</>;
	}
	return null;
}
